// Render-side view of the simulation. Intentionally flat & cheap to copy / serialize.
// Per body: position(3) + quaternion(4) = 7 floats.

export type TeamId = 'red' | 'blue';

export interface CharacterSnapshot {
  id: number;
  bones: Float32Array;     // length = boneCount * 7
  boneCount: number;
  hp: number;              // 0..CONFIG.character.hpMax — long-term health
  ko: number;              // 0..CONFIG.character.koMax — wakeness (instant-KO bar)
  koStunRemaining: number; // seconds of flopped-ragdoll stun left; >0 = KO'd right now
  wasted: boolean;         // HP hit 0 → finished for the round
  stamina: number;         // 0..1
  staminaLocked: boolean;  // true while locked out waiting for refill
  stunRemaining: number;   // seconds
  knockedOut: boolean;
  ringedOut: boolean;
  facingYaw: number;
  pelvisX: number;
  pelvisY: number;
  pelvisZ: number;
  swingPhaseL: number;     // 0 idle, 0..1 windup, 1..2 follow
  swingPhaseR: number;
  grabbing: boolean;
  // True when BOTH of this character's hands are attached to the SAME victim
  // — the "holding them up in front of me" state. Read by AI to time throws.
  doubleHolding: boolean;
  // Wriggle bar — only meaningful while being grabbed (bar snaps back to
  // max the instant all hands are released). HUD fades the bar in/out on
  // `beingGrabbed` transitions.
  wriggle: number;         // 0..CONFIG.character.grab.wriggleMax
  beingGrabbed: boolean;   // true while any attacker has ≥1 hand on us
  // True when both feet are airborne — the same condition that makes the
  // JumpKick button trigger a kick (vs a headbutt when grounded). Exposed so
  // the mobile overlay can swap its button label, and any future HUD that
  // wants to surface the same affordance can reuse it.
  bothAir: boolean;
  // True while the character is in swim mode (sim crossed the 50% submersion
  // threshold). Read by the render-side follow-cam to switch to head-tracking
  // and bypass the safety floor; also gates the underwater post-pass.
  isSwimming: boolean;
}

export interface ImpactEvent {
  victimId: number;
  attackerId: number;
  attackerBone: number;    // the bone that landed the hit (hand / foot / head)
  x: number; y: number; z: number;
  strength: number;
  accuracy: number;        // 0..1 — how cleanly the hit landed
}

export interface FootstepEvent {
  characterId: number;
  side: 'L' | 'R';
  x: number; y: number; z: number;  // foot-landing world position
}

export interface SwingStartEvent {
  characterId: number;
  side: 'L' | 'R';
  x: number; y: number; z: number;  // hand world position at press
}

/** Splash SFX trigger. Emitted on the rising edge of the swim state (when
 *  pelvis downward velocity is high enough) and on each debounced stroke
 *  while paddling. Position is the player's pelvis x/z at the water surface. */
export interface SplashEvent {
  characterId: number;
  x: number; y: number; z: number;
}

export interface SimState {
  tick: number;
  characters: CharacterSnapshot[];
  impacts: ImpactEvent[];   // events since last snapshot (read & cleared by render)
  footsteps: FootstepEvent[]; // foot-plant events (render plays positional audio)
  swings: SwingStartEvent[];  // punch initiations (render plays swing whoosh)
  splashes: SplashEvent[];    // swim entry / stroke events (render plays splash sfx)
  roundOver: boolean;
  // Which team won the round (or null for a draw — all characters out in the
  // same tick). Replaces the old `winnerId: number` — round outcomes are now
  // team-scoped, not character-scoped.
  winningTeam: TeamId | null;
  // Static team mapping for the current match (doesn't change across rounds).
  // The HUD reads this to colour name tags / panels per-character.
  teamById: Record<number, TeamId>;
}

export function emptySnapshot(id: number, boneCount: number): CharacterSnapshot {
  return {
    id,
    bones: new Float32Array(boneCount * 7),
    boneCount,
    hp: 100,
    ko: 1,
    koStunRemaining: 0,
    wasted: false,
    stamina: 1,
    staminaLocked: false,
    stunRemaining: 0,
    knockedOut: false,
    ringedOut: false,
    facingYaw: 0,
    pelvisX: 0,
    pelvisY: 0,
    pelvisZ: 0,
    swingPhaseL: 0,
    swingPhaseR: 0,
    grabbing: false,
    doubleHolding: false,
    wriggle: 1,
    beingGrabbed: false,
    bothAir: false,
    isSwimming: false
  };
}
